import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Database, Loader2 } from 'lucide-react';
import { CenteredSelect } from './CenteredSelect';
import { ContextCell } from './PartImageCells';
import { ImageLightbox } from './ImageLightbox';
import { MeshPanel, panelTitle } from './MeshPanel';
import { MeshRunStrip, useShownFailure, useShownRun } from './MeshRunStrip';
import { FailedRunPanel } from './FailedRunPanel';
import { MeshPlaceholder } from './MeshPlaceholder';
import { MeshGenerateControls } from './MeshGenerateControls';
import { ProductImport } from './ProductImport';
import { useSubmodelOptions } from './SubmodelPicker';
import { VIEWER_BOX } from './PartViewer';
import { DeleteRunButton, MeshStage } from './MeshStage';
import { CropStrip } from './CropStrip';
import { partCropsOf, useProductPartCrops } from './productPartCrops';
import { useSubmodelPhotos } from './contextImages';
import { listingImageSrc } from './panels/NoticeProductImages';
import { bumpProductRuns, useProductRuns } from './productRuns';
import { useSubmodelFilter } from './submodelFilterStore';
import { useProductLabel } from './ProductColumn';
import { getParts } from './partsRun';
import { WrittenDimensionsPanel } from './WrittenDimensionsPanel';
import { ProductOverlaps } from './ProductOverlaps';
import { useCatalogSwitch } from './catalogSwitch';
import { componentRows, useCutGuard } from './productCut';
import { useCatalogItems } from './catalogItems';
import { repaintKept, usePartMaterials } from './partMaterialRun';
import { ensureCategories, useCategories } from './catalogCategories';
import { answerOf, materialsFor, useMaterialsRun } from './materialsRun';
import { directionLabel } from './viewDirections';
import {
  deleteJob, errorOnARow, falError, fetchStatus3D, meshSrc, meshStamp,
  type Job3D, type Status3D,
} from './threeDApi';
import { useSubmodels } from './listingStore';
import { useLanguage } from '../../contexts/LanguageContext';
import { WrittenCallBadge } from './WrittenCallBadge';
import {
  builtBoxOf, defaultPasses, engineLabelOf, engineModelKey, writtenEngineOf, writtenModelLabel,
  generateWrittenMesh, isWrittenRun, newRunToken,
  passChoices, generationTook, generationStages, passGroup, overlapMeshSrc, overlapsOf, stopWritten, dimensionsOf, writtenParts, writtenPartsOf, partSizeOfMesh, checksOf, writtenInputsOf, writtenSize, writtenSizeOf, defaultEffort, effortChoices, withEffort,
} from './openaiBlenderApi';
import { IS_DEMO, IS_LOCKED } from '../../apiBase';
import { getProductSize } from './dimensionsRun';
import {
  NO_PICKS, endRun, getGeneratorChoice, runInFlight, setGeneratorChoice, startRun,
  setRunProgress, useGeneratorChoice, useRunsInFlight,
} from './generatorMemory';

// THE WHOLE PRODUCT AS ONE MESH — Phase 5's first segment, before "All parts".
//
// Every other segment of that phase reconstructs a PIECE: a leg, a rail, a screw, each from
// a photograph the Parts DB holds of it, each landing on that part's catalog row. This one
// reconstructs the assembled chair, and it is a different question with different inputs —
// the only photographs of the finished piece are the listing's, and the finished piece is
// not a row of the Parts DB.
//
// So it is a panel of its own rather than a mode of ThreeDStep's: no Parts-DB switch (there
// is no item to switch it onto), no CAD upload, no lathe. Its one measurement is the
// product's overall size, read off the listing's dimension drawing in Phase 2 beside the
// parts' (see getProductSize). What is left is the two questions this panel
// exists for — which photograph, and what came back — which are the two the part's card
// asks, drawn in the same two halves so they read as one phase.
//
// WHAT ITS RUNS ARE: a written model on listing photographs, marked in their params with the
// document and the submodel they are of (see productRunsOf). Nothing on a run's row could
// say it — item_id is null for a listing photo, that photo belonging to a notice rather
// than to a part.

const NONE_PICKED: Set<number> = new Set();

export function ProductModelStep({ docId, locked }: {
  docId: string;
  // The run that holds this phase is paused, or this build may not spend.
  locked?: boolean;
}) {
  const { t } = useLanguage();
  // What the product is CALLED — the category and the product name.
  const label = useProductLabel(docId);
  // WHICH COLOUR, from the dropdown at the top of the phase — the same shared value the
  // parts under it follow. It is part of what a product run IS: a blue chair and a grey one
  // are reconstructed from two sets of photographs and are two meshes.
  const submodel = useSubmodelFilter(docId);
  // The colourway's name, for the file a download saves: "Chaise Josette (bois massif foncé)".
  const colourway = useSubmodelOptions(docId).find((o) => o.id === submodel)?.name;
  // Null until the server has answered — see the `pictures` below, which spins rather than
  // saying there are none.
  const { photos: loadedPhotos, failed: photosFailed } = useSubmodelPhotos(docId);
  const photos = useMemo(() => loadedPhotos ?? [], [loadedPhotos]);
  // THE PARTS A WRITTEN RUN IS TOLD, each with what Phase 2 says it is made of in this colour.
  //
  // COMPONENTS ONLY, which needs the ontology: it says which rows of a parts list are the
  // furniture and which are the screws holding it together, and it arrives over the network.
  // Read off the module cache before it lands, every category resolves to the default —
  // component — and the prompt asks for a mesh of each washer. Subscribed here so the badge
  // is right, and awaited by the press (see `sending`), a run being one prompt built once and
  // paid for rather than something that can be right one render later.
  const partMaterials = usePartMaterials(docId);
  const categories = useCategories();
  const sentParts = useMemo(
    () => writtenParts(getParts(docId), partMaterials.bySub[submodel], categories),
    [docId, partMaterials, submodel, categories],
  );
  const sending = async () => writtenParts(
    getParts(docId), partMaterials.bySub[submodel], await ensureCategories(),
  );
  // AND WHAT THE FIRST SUBPHASE READ THEM TO BE MADE OF. Subscribed rather than read once:
  // reading the materials next door must change what the badge here says the next press
  // would send, without this panel being remounted.
  const materialsRead = useMaterialsRun(docId);
  const materials = answerOf(materialsRead, submodel).materials;

  const [probe, setProbe] = useState<Status3D | null>(null);
  // WHAT IS RUNNING HERE, and what was chosen — kept outside this panel, which is thrown away
  // whenever a part is opened. See generatorMemory: a run left behind comes back with its spinner
  // and its Stop, and the photographs, model and passes come back as they were chosen.
  const runKey = `product:${submodel}`;
  const flight = useRunsInFlight(docId)[runKey];
  const busy = !!flight;
  const choice = useGeneratorChoice(docId, 'product');
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState<string | null>(null);
  // WHICH SET THE ZOOM STEPS THROUGH: the photographs the run on screen was made from, when it was
  // opened from that run's strip, or every photograph of this colour, when opened from the picker.
  const [zoomSet, setZoomSet] = useState<'sent' | 'photos'>('photos');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // AND THE CATALOG ROW BEHIND EACH OF THEM, which is what lets the press below keep the
  // product's components as the parts' own models. One rule for "which row is this part"
  // (partIdentity) and one rule for "which parts is a product built from" (writtenPartRows),
  // both already applied elsewhere on this page.
  //
  // A part with no row in the database is left out rather than invented: there is nowhere to
  // file its mesh, and a mesh filed under a row somebody would have to create is a row nobody
  // asked for.
  const { items: catalogItems, loaded: catalogLoaded } = useCatalogItems();
  const componentItems = () =>
    componentRows(getParts(docId), categories, catalogItems, catalogLoaded);
  // KEEP A MESH AS THE PRODUCT'S 3D MODEL — one per submodel, the same switch a part's runs have
  // (see useCatalogSwitch). The runs are re-read, the list being a shared store the part selector
  // reads too.
  //
  // AND ITS COMPONENTS WITH IT: a written product is one mesh per part, so the press that says
  // "this is the chair" says what the seat and the rails are too. See _keep_pieces.
  //
  // AND IT IS EVERY COLOURWAY'S MODEL: one shape, the colourways the paint over it (see
  // colourwayPaint). So keeping it records every colourway's paint for it — each its own reading.
  const submodels = useSubmodels(docId);
  const cutSwitch = useCatalogSwitch((saved) => {
    bumpProductRuns(docId);
    if (!saved.inCatalog) return;
    for (const s of submodels.length ? submodels.map((one) => one.id) : [submodel]) repaintKept(docId, s);
  }, setError, componentItems);
  // AND IT ASKS FIRST WHERE THE PRESS WOULD LOSE DRILLED HOLES — see useCutGuard.
  const components = componentItems();
  const { catalog, dialog: cutDialog } = useCutGuard(cutSwitch, components);
  // WHICH PHOTOGRAPH THE RUN IS MADE FROM, and which mesh is on screen. Both are held by id
  // and both fall back rather than being cleared: a submodel with no such id resolves to its
  // own first picture and its own newest mesh, which is what changing colour should show.
  const pickedIds = useGeneratorChoice(docId, runKey).picked ?? NO_PICKS;
  const setPickedIds = (next: (was: number[]) => number[]) => setGeneratorChoice(
    docId, runKey, { picked: next(getGeneratorChoice(docId, runKey).picked ?? []) },
  );
  // WHETHER THE MESH IS OPEN BIG. The page's, not the viewer's — the same arrangement the
  // part's panel has, and for the same reason: what somebody doing this is doing is
  // comparing, and a modal that closed itself whenever the list beneath it changed would
  // break the comparison in half.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    void fetchStatus3D().then(setProbe).catch(() => setProbe(null));
  }, []);

  // This notice's product runs in this colour, shared with the selector above — which wears
  // a mark saying whether the product has a model, and must not go on saying "none" after
  // this panel has made one. See productRuns.
  const { runs: loadedRuns, failed: runsFailed } = useProductRuns(docId, submodel);
  const runs = loadedRuns ?? [];
  const ready = runs.filter((j) => j.ready);
  const { shown, setShownId, showRun } = useShownRun(ready);
  // A RUN THAT MADE NOTHING, where its mesh would be — see useShownFailure.
  const failedRun = useShownFailure(runs, busy);
  // A TILE PRESSED WHILE A RUN IS GOING shows its model; until then, and again once the run's own
  // tile is pressed, the box waits for the run (see ThreeDStep, where this began).
  const [peek, setPeek] = useState(false);
  useEffect(() => { if (busy) setPeek(false); }, [busy]);
  // WHICH COLLISIONS ARE LAID OVER THE MODEL, by their solid's number — the Collisions table's rows,
  // pressed. ONE SELECT ALL FOR EVERY VERSION: pressed on one, every version shows all of its own
  // collisions (or none), so paging through the versions compares them lit the same way. A row
  // pressed by hand is that version's alone — its numbers name its own solids — until the next
  // Select all or Deselect all, which sets every version again. Kept while the page is.
  const [allOverlaps, setAllOverlaps] = useState(false);
  const [pickedByJob, setPickedByJob] = useState<Map<number, Set<number>>>(new Map());
  const everyOverlap = (job: typeof shown) => new Set(
    (overlapsOf(job) ?? []).flatMap((o) => (o.mesh != null ? [o.mesh] : [])),
  );
  const overlapsPicked = !shown ? NONE_PICKED
    : pickedByJob.get(shown.id) ?? (allOverlaps ? everyOverlap(shown) : NONE_PICKED);
  const setOverlapsPicked = (next: (was: Set<number>) => Set<number>) => {
    if (!shown) return;
    const id = shown.id;
    setPickedByJob((all) => new Map(all).set(id, next(overlapsPicked)));
  };
  // ITS MESHES BEING REWRITTEN — a materials apply, or a part's material followed onto them.
  const repainting = useMaterialsRun(docId).applying.has(submodel);
  // WHICH VIEW THE TWO VIEWERS SHARE — the run, by its last pass's id, which every pass of
  // it agrees on. The part's panel computes it the same way and for the same reason: the
  // small viewer and the big one are two mountings of one component, and the camera crosses
  // between them under this key. See PartViewer's keepView.
  const viewKey = shown
    ? String(passGroup(shown, ready).slice(-1)[0]?.id ?? shown.id)
    : undefined;
  // Nothing left to be expanded ON.
  useEffect(() => { if (!ready.length) setExpanded(false); }, [ready.length]);

  // THE PHOTOGRAPHS THE RUN ON SCREEN WAS MADE FROM, and nothing else — the reference row over the
  // big viewer. It showed every photograph of this colour, which says nothing about the mesh: a
  // written run is shown the few that were ticked. Off the run itself (see
  // writtenInputsOf), so every run already made shows its own, and the row changes as the passes
  // and the runs are stepped through.
  const sent = shown ? writtenInputsOf(shown) : [];
  const sentSrcs = sent.map((one) => listingImageSrc(one.imageId));
  // AND THE CLOSE-UPS IT WAS SENT after them, where its toggle was on — in the same row, each
  // tile its box on its photograph. See productPartCrops.
  const sentCrops = shown ? partCropsOf(shown) : [];
  const sentZoomSrcs = [...sentSrcs, ...sentCrops.map((c) => c.url)];

  // The direction of the photograph the zoom is standing on, in the reader's own words: what the
  // run recorded, where it was opened from the run, else Phase 1's reading of the photograph.
  const zoomedView = (() => {
    if (!zoom) return undefined;
    const recorded = zoomSet === 'sent'
      ? sent.find((one) => listingImageSrc(one.imageId) === zoom)?.view
      : undefined;
    const view = recorded || photos.find((p) => listingImageSrc(p.id) === zoom)?.view;
    return view ? directionLabel(view) : undefined;
  })();

  // THE MODEL THAT WRITES IT — none on a server without an OpenAI key, whose button is dead.
  const writtenModel = writtenEngineOf(probe);
  // HOW MANY TIMES THE WRITTEN ENGINE MODELS IT — the same choice the part's generator
  // offers, in the same place relative to the same button. See ThreeDStep, where the
  // argument for both is written out.
  // The server's default until somebody picks a number, and then what they picked.
  const passes = choice.passes ?? defaultPasses(probe);
  const setPasses = (value: number) => setGeneratorChoice(docId, 'product', { passes: value });
  // HOW HARD IT THINKS: what was chosen, where the chosen model takes it, else its default.
  const efforts = writtenModel ? effortChoices(probe, writtenModel) : [];
  const effort = choice.effort && efforts.includes(choice.effort)
    ? choice.effort
    : (writtenModel ? defaultEffort(probe, writtenModel) : '');
  const setEffort = (value: string) => setGeneratorChoice(docId, 'product', { effort: value });
  // WHETHER THE PARTS GO UP CLOSE TOO — Phase 2's selected crops, after the photographs. A
  // switch and not a default, so the same product can be built with and without them and the
  // two meshes compared. Off until somebody turns it on.
  const partCrops = useProductPartCrops(docId);
  const withCrops = !!choice.crops;
  const setWithCrops = (on: boolean) => setGeneratorChoice(docId, 'product', { crops: on });

  // WHICH PHOTOGRAPHS THE RUN IS MADE FROM, in the order they were pressed — the same
  // arrangement the part's generator has. As many as four: no one picture of a chair holds
  // its back and its underside.
  const chosen = useMemo(() => {
    const kept = pickedIds
      .map((id) => photos.find((p) => p.id === id))
      .filter((p): p is typeof photos[number] => !!p);
    return kept.length ? kept : (photos[0] ? [photos[0]] : []);
  }, [pickedIds, photos]);
  const input = chosen[0] ?? null;
  const pick = (id: number) => setPickedIds((was) => {
    const now = was.length ? was : chosen.map((p) => p.id);
    if (!now.includes(id)) return [...now, id];
    // Down to the last one standing: there is nothing to model from with no picture, and
    // the button would go dead under the pointer that had just been choosing what to press.
    return now.length > 1 ? now.filter((x) => x !== id) : now;
  });

  // The same tiles the parts table's CONTEXT row draws, from the same list — and carrying
  // the same numbering the prompt uses, so what is on screen is the list the model is given.
  const tiles = useMemo(
    () => photos.map((p) => ({
      url: listingImageSrc(p.id),
      view: p.view,
      selected: chosen.some((c) => c.id === p.id),
      at: chosen.length > 1 ? chosen.findIndex((c) => c.id === p.id) + 1 : 0,
    })),
    [photos, chosen],
  );

  // THE RUN IN FLIGHT, CALLED OFF — see the part's generator, where the same pair sits beside
  // the same button.
  const stopRun = () => {
    const going = runInFlight(docId, runKey);
    if (!going?.token || !going.abort) return;
    void stopWritten(going.token).catch(() => {});
    going.abort.abort();
  };

  const run = async () => {
    if (busy || !input || !writtenModel) return;
    // Captured now: the colour may change under a run that takes minutes.
    const key = runKey;
    startRun(docId, key, { token: null, abort: null });
    setError('');
    try {
      // How many passes of this run the page has already read in — see onProgress below.
      let landed = 0;
      const going = { token: newRunToken(), abort: new AbortController() };
      startRun(docId, key, going);
      const job = await generateWrittenMesh(
        'listing', chosen.map((p) => ({ id: p.id, view: p.view })), writtenModel, {
          subject: label,
          product: { docId, submodel },
          stop: { token: going.token, signal: going.abort.signal },
          passes,
          // The product's overall size off Phase 2's drawing, to be built at.
          size: writtenSize(getProductSize(docId)),
          effort: effort || undefined,
          onProgress: (progress) => {
            setRunProgress(docId, key, progress);
            // EACH PASS AS IT LANDS. A run saves every pass the moment it is built, so the
            // strip can hold pass 2 while pass 3 is being thought about — and the viewer
            // goes to it, the way it goes to the run at the end. Only on the way UP: the
            // list is re-read when the count grows, not on every poll.
            if (progress.saved.length > landed) {
              landed = progress.saved.length;
              bumpProductRuns(docId);
              setShownId(progress.saved[progress.saved.length - 1]);
            }
          },
          // What it is made of, off Phase 2's parts list.
          parts: await sending(),
          // AND WHAT THOSE PARTS ARE MADE OF, off this phase's first subphase — every
          // material's colour, roughness and metallic, and its grain where the patches
          // have been measured. The build no longer judges them: read once for the
          // product, spent by every mesh of it.
          materials: materialsFor(docId, submodel).materials,
          ...(withCrops ? { partCrops } : {}),
        },
      );
      bumpProductRuns(docId);
      // Straight to what was just paid for, rather than leaving the viewer on whatever it
      // was showing: the newest run is the head of the list anyway, but only while nobody
      // has chosen another one to look at. Null where the server gave up instead of
      // building, and there is nothing to look at.
      if (job) setShownId(job.id);
    } catch (e) {
      // A run somebody stopped is not a run that failed.
      if (!axios.isCancel(e)) setError(falError(e, t('notice.3d.failed')));
      bumpProductRuns(docId);
    } finally {
      endRun(docId, key);
    }
  };

  const remove = async (job: Job3D) => {
    setDeletingId(job.id);
    try {
      await deleteJob(job.id);
      bumpProductRuns(docId);
    } finally {
      setDeletingId(null);
    }
  };

  // WHAT THE LAST PRESS OF THE DATABASE SWITCH DID WITH THE COMPONENTS, where that is not
  // simply what it looks like it did: the pieces a switch turned off took back with it, or
  // the reason there were none to cut out.
  //
  // NOTHING IS SAID ABOUT A PRESS THAT WORKED. Keeping the components is what keeping the
  // product MEANS now, and a line counting them was a receipt for the ordinary case — the
  // switch itself is lit, and the parts have their models. What is left is the two answers
  // the switch cannot show: the pieces that went nowhere, and the ones taken back out.
  const cut = catalog.pieces;
  const piecesNote = !cut ? null
    : cut.error ? t('notice.3d.piecesFailed').replace('{why}', cut.error)
      : cut.forgot ? t('notice.3d.piecesDropped').replace('{n}', String(cut.forgot))
        : null;

  // The heading over the mesh — the panel's, and the modal's when it is opened big.
  const viewTitle = panelTitle(t('notice.3d.boxView'), withEffort(engineLabelOf(shown, probe), shown));

  return (
    <div className="flex w-full flex-col items-center gap-5">
      {cutDialog}
      {/* No OpenAI key: nothing here can make a model. Not said where nothing can be run anyway
          (the demo, the sandbox: IS_LOCKED), where it read as a fault in a page that is fine. */}
      {probe && !writtenModel && !IS_LOCKED && (
        <p className="text-sm text-rose-600">{t('notice.3d.noKey')}</p>
      )}
      {error && !errorOnARow(error, runs) && (
        <p className="max-w-xl text-center text-sm text-rose-600">{error}</p>
      )}
      {/* WHAT THE LAST PRESS OF THE DATABASE SWITCH DID WITH THE COMPONENTS.
          Said rather than left to be discovered a phase later: the whole of this feature is
          that five more meshes are now in the database under five other rows, and a press
          that looks like it only kept the chair is a press nobody knows to check the parts
          for. The pieces it could NOT place are named — a component whose row the parts list
          does not hold is the one case where the answer is incomplete, and it is the parts
          table that has to be fixed, not this button. */}
      {(piecesNote || !!cut?.skipped.length) && (
        <p className="max-w-xl text-center text-sm text-slate-500">
          {piecesNote}
          {!!cut?.skipped.length && (
            <span className="block text-amber-600">
              {t('notice.3d.piecesSkipped').replace('{names}', cut.skipped.join(', '))}
            </span>
          )}
        </p>
      )}

      {/* A CARD EACH, the mesh first — see MeshPanel, which draws them and where the
          argument for splitting the one rectangle in two is written out. The part's card is
          the same two panels in the same order, and they are twins on purpose: the answer
          on top, the machinery for making one under it. */}
      <MeshPanel
        title={viewTitle}
        // WHAT IS ON SCREEN, as every subphase's card says it: here the one thing there is, marked
        // as the others are once its model is kept.
        corner={(
          <CenteredSelect
            look="pill"
            big
            value="product"
            options={[{
              value: 'product', label,
              ...(runs.some((j) => j.inCatalog)
                ? { icon: <Database className="h-3.5 w-3.5 shrink-0 text-blue-600" /> } : {}),
            }]}
            onChange={() => {}}
            ariaLabel={label}
          />
        )}
      >
        {busy && !peek ? (
          <MeshPlaceholder
            label={t('notice.3d.generating')}
            icon={<Loader2 className="h-8 w-8 animate-spin text-slate-300" />}
            className={`w-full ${VIEWER_BOX}`}
          />
        ) : failedRun.failure ? (
          <FailedRunPanel
            job={failedRun.failure}
            className={`w-full ${VIEWER_BOX}`}
            deleting={deletingId === failedRun.failure.id}
            onDelete={() => void remove(failedRun.failure!)}
            badge={(
              <WrittenCallBadge
                subject={label}
                photos={writtenInputsOf(failedRun.failure).map((one) => ({
                  url: listingImageSrc(one.imageId), view: one.view,
                }))}
                title={t('notice.3d.runFailedCall')}
                run={failedRun.failure}
                runs={[failedRun.failure]}
                model={(failedRun.failure.model || '').replace('openai/', '')}
                size={writtenSizeOf(failedRun.failure)}
                parts={writtenPartsOf(failedRun.failure)}
              />
            )}
          />
        ) : shown ? (
          /* THE SAME PAIR THE PART HAS: the panel-sized viewer, the button that opens it big,
             and the modal over the page — from MeshStage, so a product cannot go on being the
             one mesh in this app nobody could look at closely. It had a viewer and no way to
             expand it, which is not a decision anybody took about products; it is what
             happens when the same thing is written in two files.

             WHAT IT HANDS IN IS WHAT A PRODUCT HAS: the footer acts on the RUN, and the reference
             row is the photographs it was modelled from. The database switch is not handed in
             as a slot — the stage draws it, for a part and a product alike.

             AND ITS OVERALL SIZE, which is the only thing that scales the mesh: a written run
             is in a model's guessed metres, which is not a measurement. Never `trueScale` — that is for a supplier's
             CAD file, and a written run's metres are an estimate a printed number corrects.
             Absent, the box says "?" rather than print file units as millimetres. */
          <MeshStage
            job={shown}
            busy={repainting}
            catalog={catalog}
            src={meshSrc(shown.id, meshStamp(shown))}
            label={label}
            fileName={colourway ? `${label} (${colourway})` : label}
            dims={getProductSize(docId)}
            // AND EACH MESH'S, off the parts this run was told — the "wanted" row on its own box.
            pieceWanted={(mesh) => partSizeOfMesh(mesh, writtenPartsOf(shown))}
            keepView={viewKey}
            // THE ONE MESH IN THIS APP THAT IS AN ASSEMBLY OF OTHER THINGS: the whole product,
            // whose meshes are its component parts. Everywhere else the file's several meshes
            // are how one object was built — see PartViewer's `explodable`.
            explodable
            // THE PRESSED COLLISIONS, red over the model — see ProductOverlaps.
            // Each on the two meshes it is between, so exploded it follows both.
            overlays={[...overlapsPicked].sort((a, b) => a - b).map((n) => {
              const o = overlapsOf(shown)?.find((one) => one.mesh === n);
              return { url: overlapMeshSrc(shown.id, n), colour: '#ef4444', on: o ? [o.a, o.b] : [] };
            })}
            // What this run measured — see mmScale. A product usually has a printed size and
            // never reaches this, which is exactly the order it should be in.
            builtMm={builtBoxOf(shown)}
            expanded={expanded}
            onExpanded={setExpanded}
            passes={{ of: ready, shown, onShow: setShownId, onDelete: (j) => void remove(j), deletingId }}
            heading={viewTitle}
            took={generationTook(shown, ready)}
            stages={generationStages(shown, ready)}
            checks={checksOf(shown)}
            className={`w-full ${VIEWER_BOX}`}
            caption={isWrittenRun(shown) ? (
              <span className="inline-flex translate-y-0.5 align-middle">
                <WrittenCallBadge
                  subject={label}
                  photos={writtenInputsOf(shown).map((one) => ({
                    url: listingImageSrc(one.imageId), view: one.view,
                  }))}
                  run={shown}
                  runs={ready}
                  model={(shown.model || '').replace('openai/', '')}
                  size={writtenSizeOf(shown)}
                  parts={writtenPartsOf(shown)}
                />
              </span>
            ) : undefined}
            strip={sentSrcs.length > 0 && (
              <CropStrip
                crops={[
                  ...sentSrcs.map((url) => ({
                    url, bbox: [0, 0, 1, 1] as [number, number, number, number],
                  })),
                  ...sentCrops.map((c) => ({ url: c.url, bbox: c.bbox })),
                ]}
                alt={label}
                onOpen={(crop) => { setZoomSet('sent'); setZoom(crop.url); }}
              />
            )}
            // THE NUMBERS IT WAS BUILT TO, and only those.
            //
            // THE MATERIALS ARE NOT HERE ANY MORE, and that is the point of them: what the
            // product is made of is read once for the product, in this phase's first subphase
            // (see MaterialsPanel), and handed to every build. A table of them under one mesh
            // was a table of what THAT mesh happened to be built with, in a modal, per run —
            // which is exactly the arrangement that let two meshes of one chair disagree
            // about the colour of its seat with nowhere to see that they did.
            footer={isWrittenRun(shown) && dimensionsOf(shown).length > 0 ? (
              <div className="flex w-full flex-col items-center gap-5">
                <WrittenDimensionsPanel
                  job={shown}
                  disabled={locked}
                  onRebuilt={(job) => {
                    bumpProductRuns(docId);
                    setShownId(job.id);
                  }}
                />
              </div>
            ) : undefined}
            corner={(
              <DeleteRunButton
                label={t('notice.3d.deleteModel')}
                busy={deletingId === shown.id}
                onClick={() => void remove(shown)}
              />
            )}
          />
        ) : loadedRuns === null && !busy ? (
          // NOT ASKED BACK YET: a spinner, or the failure — never "no model", which is only
          // true once the server has said so.
          runsFailed ? (
            <p className="text-center text-sm text-rose-600">{t('notice.models.runsFailed')}</p>
          ) : (
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
          )
        ) : (
          <p className="text-center text-sm text-slate-400">
            {busy ? t('notice.3d.generating') : t('notice.models.noProductMesh')}
          </p>
        )}
        {/* WHERE ITS PARTS RUN INTO EACH OTHER — see ProductOverlaps. Written runs only: they are
            the ones Blender builds, and measures, part by part. */}
        {shown && isWrittenRun(shown) && (
          <ProductOverlaps
            docId={docId}
            job={shown}
            picked={overlapsPicked}
            onPickAll={(meshes) => {
              setAllOverlaps(meshes.length > 0);
              setPickedByJob(new Map());
            }}
            onPick={(n) => setOverlapsPicked((was) => {
              const next = new Set(was);
              if (!next.delete(n)) next.add(n);
              return next;
            })}
            locked={locked}
            onFixed={(job) => {
              bumpProductRuns(docId);
              setShownId(job.id);
            }}
          />
        )}

        {/* Every mesh this product has had, under the one it is showing — the same strip the
            part's panel draws, from the same component. It was a flat row of "#89" here and
            rows per engine there, with a pass count on the labels, because the two were
            written twice and only one of them was improved. The database switch on every tile
            included, which the strip draws itself. */}
        <MeshRunStrip
          ready={ready}
          shown={shown}
          // THE RUN IN FLIGHT, drawn as a tile the moment the button is pressed — the same
          // arrangement the part's strip has, from the same component.
          pending={busy
            ? {
              model: engineModelKey(writtenModel ?? ''),
              label: writtenModelLabel(probe, writtenModel ?? ''),
              // The newest pass this run has landed, for the tile to open. `ready` is
              // newest-first, so the first of them that this run saved is the latest.
              made: ready.find((j) => flight?.progress?.saved?.includes(j.id)) ?? null,
            }
            : null}
          engineLabel={(j) => engineLabelOf(j, probe)}
          repainting={repainting}
          onSelect={(j) => { setPeek(true); failedRun.leave(); setShownId(j.id); }}
          onOpenRun={(j) => { setPeek(true); failedRun.leave(); showRun(j); }}
          onOpenPending={() => setPeek(false)}
          failed={failedRun.failures}
          failedShown={failedRun.failure}
          onShowFailed={failedRun.show}
          catalog={catalog}
        />

        {/* (The delete lived here, under the strip. It is in the expanded view's own corner
            now, which is where the part's has always been: the corner is for what acts on
            the RUN rather than on its geometry, and one place for one control beats two
            places that will word it differently.) */}
      </MeshPanel>

      {/* OR THE MAKER'S OWN MODEL, from a file: the badge beside generation — see ProductImport. */}
      {!IS_DEMO && (
        <ProductImport
          docId={docId}
          submodel={submodel}
          parts={components}
          shown={shown}
          onImported={(job) => { bumpProductRuns(docId); setShownId(job.id); }}
        />
      )}
      {/* ITS GENERATION IS THE PHASE'S BADGE, over the selector — see MeshGenerateControls. */}
      {!IS_DEMO && (
        <MeshGenerateControls
          scope="product"
          passes={passes}
          passChoices={passChoices(probe)}
          onPasses={setPasses}
          effort={effort}
          effortChoices={efforts}
          onEffort={setEffort}
          busy={busy}
          progress={flight?.progress}
          since={flight?.startedAt}
          // This panel's own list of what would stop a press: a picture, the key, and the page
          // not being locked.
          // AND THE MATERIALS READ. A build is handed them now rather
          // than judging its own (see MaterialsPanel), so a press before they are read would
          // build the chair in the neutral grey written_toolkit falls back to — a silent wrong
          // answer, and the one thing a missing input must not be.
          canRun={!locked && !!input && !!writtenModel && materials.length > 0
            // Asked for and none to send: a missing input shows as missing.
            && (!withCrops || partCrops.length > 0)}
          pictures={loadedPhotos === null ? (
            photosFailed ? (
              <p className="mb-1 text-center text-sm text-rose-600">
                {t('notice.models.photosFailed')}
              </p>
            ) : (
              <Loader2 className="mx-auto mb-1 h-5 w-5 animate-spin text-slate-400" />
            )
          ) : photos.length === 0 ? (
            /* No product photograph for this colour. Phase 1 is where they are found and
               kept, and saying so is more use than an empty row of nothing. */
            <p className="mb-1 text-center text-sm text-slate-400">
              {t('notice.models.noProductPhotos')}
            </p>
          ) : (
            <div className="mb-1 flex w-full flex-col items-center gap-2">
              <p className="text-center text-xs text-slate-400">
                {t('notice.3d.pickInputs')}
              </p>
              {/* Wide, not two across: a submodel's ten angles are worth seeing side by side. */}
              <ContextCell
                context={tiles}
                maxAcross={6}
                onZoom={(url) => { setZoomSet('photos'); setZoom(url); }}
                onToggleContext={(url) => {
                  const at = photos.find((p) => listingImageSrc(p.id) === url);
                  if (at) pick(at.id);
                }}
              />
            </div>
          )}
          options={(
            <div className="flex flex-col items-center gap-1">
              <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={withCrops}
                  onChange={(e) => setWithCrops(e.target.checked)}
                  disabled={busy}
                  className="h-3.5 w-3.5 accent-blue-600"
                />
                {t('notice.3d.includeCrops')}
                {withCrops && partCrops.length > 0 && (
                  <span className="tabular-nums text-slate-500">({partCrops.length})</span>
                )}
              </label>
              {withCrops && !partCrops.length && (
                <p className="max-w-xs text-center text-[11px] text-amber-600">
                  {t('notice.3d.includeCropsNone')}
                </p>
              )}
            </div>
          )}
          onRun={() => void run()}
          onStop={stopRun}
          call={{
            subject: label,
            photos: chosen.map((p) => ({ url: listingImageSrc(p.id), view: p.view })),
            model: writtenModel ?? undefined,
            size: writtenSize(getProductSize(docId)),
            parts: sentParts,
            materials,
            crops: withCrops ? partCrops : undefined,
          }}
        />
      )}

      {/* WHAT THE PICTURE IS OF, beside how far through the set it is: Phase 1 classified
          these photographs and the zoom is the one place they are big enough to disagree
          with the answer. Omitted for a picture nothing classified, which is not the same
          as one classified as no particular view. */}
      <ImageLightbox
        src={zoom}
        srcs={zoomSet === 'sent' ? sentZoomSrcs : photos.map((p) => listingImageSrc(p.id))}
        // A close-up opened from the reference row is its whole photograph, with its box on it.
        boxes={zoomSet === 'sent'
          ? sentCrops.filter((c) => c.url === zoom).map((c) => ({ box: c.bbox, label: c.part }))
          : undefined}
        view={zoomedView}
        onNavigate={setZoom}
        onClose={() => setZoom(null)}
      />
    </div>
  );
}
